import { BankOutlined, DollarOutlined, FileDoneOutlined, FileTextOutlined, MessageOutlined, TeamOutlined, UnlockOutlined } from "@ant-design/icons";
import React from "react";
import { Button, StatusText, Tooltip, Typography } from "../../../../../../../shared/frontend/ui";
import type { I18nContextValue } from "../../../app/i18n/useI18n";
import { formatDateOnly, formatDayMonth, formatMoney, formatNumber } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import type { PersonOverviewTodo } from "./usePersonOverview";
import "./personOverview.css";

const TODO_DETAIL = { reminderLevel: "reminder_level", iban: "iban", cancellation: "cancellation", cancelled: "cancelled" } as const;

type TodoAction = { icon: React.ReactNode; label: string; onClick: () => void };

type Props = {
  todos: PersonOverviewTodo[];
  text: I18nContextValue["text"];
  units: UnitsSettings;
  canUnblockAccess: boolean;
  onOpenTab: (tab: string) => void;
  onOpenAppRequest: (requestId: string) => void;
  onOpenCareCase: (caseId: string) => void;
  onUnblockAccess: () => void;
  /** Überfällige Posten direkt kassieren (Check-in) statt den Reiter zu öffnen. */
  onCollectOverdue?: () => void;
  /**
   * `chips`: Übersicht der Akte — je Vorgang ein Kurzetikett, das selbst die Aktion ist; der ganze Satz steht im
   * Tooltip. `list` (Check-in): Satz plus Aktionsknopf je Zeile.
   */
  variant?: "list" | "chips";
};

const fill = (template: string, ...values: string[]) =>
  values.reduce((result, value, index) => result.replace(`{${index}}`, value), template);

/**
 * Inhalt des Blocks „Handlungsbedarf“: nur, was jetzt eine Handlung braucht — Regeln im Backend
 * (`PersonOverviewTodoRules`). Ohne Eintrag der Leerzustand „kein Handlungsbedarf“. Je Zeile höchstens eine
 * Aktion, die direkt an die Stelle führt, an der der Vorgang erledigt wird.
 */
export function PersonOverviewTodoList({ todos, text, units, canUnblockAccess, onOpenTab, onOpenAppRequest, onOpenCareCase, onUnblockAccess, onCollectOverdue, variant = "list" }: Props) {
  const date = (value?: string | null) => (value ? formatDateOnly(value, units) : "—");

  const describe = (todo: PersonOverviewTodo): string => {
    const name = todo.name ?? "—";
    switch (todo.code) {
      case "open_items_overdue":
        return todo.count === 1
          ? fill(text("Person overview todo open item overdue"), formatMoney(todo.amount ?? 0, units), date(todo.date))
          : fill(text("Person overview todo open items overdue"), formatNumber(todo.count ?? 0, units), formatMoney(todo.amount ?? 0, units), date(todo.date));
      case "access_blocked":
        return todo.detail === TODO_DETAIL.reminderLevel && todo.name
          ? fill(text("Person overview todo access blocked reminder"), todo.name)
          : text("Person overview todo access blocked manual");
      case "sepa_mandate_missing":
        return fill(text(todo.detail === TODO_DETAIL.iban ? "Person overview todo sepa iban" : "Person overview todo sepa mandate"), name);
      case "app_request_pending":
        return fill(text(todo.detail === TODO_DETAIL.cancellation ? "Person overview todo app request cancellation" : "Person overview todo app request rest period"), date(todo.date));
      case "contract_ending":
        return fill(text(todo.detail === TODO_DETAIL.cancelled ? "Person overview todo contract cancelled" : "Person overview todo contract term end"), name, date(todo.date));
      case "rest_period_ending":
        return fill(text("Person overview todo rest period ending"), name, date(todo.date));
      case "care_follow_up_due":
        return fill(text("Person overview todo care follow up"), name, date(todo.date));
      case "no_valid_contract":
        return text("Person overview todo no valid contract");
    }
  };

  const actionOf = (todo: PersonOverviewTodo): TodoAction | null => {
    const contracts: TodoAction = { icon: <FileDoneOutlined />, label: text("Contracts"), onClick: () => onOpenTab("contracts") };
    switch (todo.code) {
      case "open_items_overdue":
        return onCollectOverdue
          ? { icon: <DollarOutlined />, label: text("Payment"), onClick: onCollectOverdue }
          : { icon: <FileTextOutlined />, label: text("Open items"), onClick: () => onOpenTab("openitems") };
      case "access_blocked":
        // Eine Mahnstufensperre endet mit dem Ausgleich der Posten; nur die manuelle Sperre wird hier aufgehoben.
        if (todo.detail === TODO_DETAIL.reminderLevel) return { icon: <FileTextOutlined />, label: text("Open items"), onClick: () => onOpenTab("openitems") };
        return canUnblockAccess ? { icon: <UnlockOutlined />, label: text("Person overview unblock access"), onClick: onUnblockAccess } : null;
      case "sepa_mandate_missing":
        return { icon: <BankOutlined />, label: text("Bank details"), onClick: () => onOpenTab("profile") };
      case "app_request_pending":
        return todo.targetId ? { icon: <MessageOutlined />, label: text("Person overview todo action request"), onClick: () => onOpenAppRequest(todo.targetId!) } : null;
      case "care_follow_up_due":
        return todo.targetId ? { icon: <TeamOutlined />, label: text("Person overview todo action care"), onClick: () => onOpenCareCase(todo.targetId!) } : null;
      case "contract_ending":
      case "rest_period_ending":
      case "no_valid_contract":
        return contracts;
    }
  };

  const shortDate = (value?: string | null) => (value ? formatDayMonth(value, units) : "—");
  const chipLabel = (todo: PersonOverviewTodo): string => {
    switch (todo.code) {
      case "open_items_overdue":
        return fill(text("Person overview chip overdue"), formatNumber(todo.count ?? 1, units), formatMoney(todo.amount ?? 0, units));
      case "access_blocked":
        return text("Person overview chip access blocked");
      case "sepa_mandate_missing":
        return text(todo.detail === TODO_DETAIL.iban ? "Person overview chip iban missing" : "Person overview chip mandate missing");
      case "app_request_pending":
        return text(todo.detail === TODO_DETAIL.cancellation ? "Person overview chip cancellation request" : "Person overview chip rest period request");
      case "contract_ending":
        return fill(text(todo.detail === TODO_DETAIL.cancelled ? "Person overview chip contract cancelled" : "Person overview chip contract term end"), shortDate(todo.date));
      case "rest_period_ending":
        return fill(text("Person overview chip rest period ending"), shortDate(todo.date));
      case "care_follow_up_due":
        return fill(text("Person overview chip care follow up"), shortDate(todo.date));
      case "no_valid_contract":
        return text("Person overview todo no valid contract");
    }
  };

  if (todos.length === 0) return <Typography.Text type="secondary">{text("Person overview no todos")}</Typography.Text>;
  if (variant === "chips") {
    return (
      <ul className="person-overview-chips">
        {todos.map((todo, index) => {
          const action = actionOf(todo);
          const tone = todo.urgent ? "error" : "warn";
          return (
            <li key={`${todo.code}-${todo.targetId ?? index}`}>
              <Tooltip title={action ? `${describe(todo)} · ${action.label}` : describe(todo)}>
                {action ? (
                  <Button size="small" className="person-overview-chip" data-tone={tone} icon={action.icon} onClick={action.onClick}>
                    {chipLabel(todo)}
                  </Button>
                ) : (
                  <span className="person-overview-chip person-overview-chip--static" data-tone={tone}>{chipLabel(todo)}</span>
                )}
              </Tooltip>
            </li>
          );
        })}
      </ul>
    );
  }
  return (
    <ul className="person-overview-todo-list">
      {todos.map((todo, index) => {
        const action = actionOf(todo);
        return (
          <li key={`${todo.code}-${todo.targetId ?? index}`} className="person-overview-todo-row">
            <StatusText color={todo.urgent ? "error" : "warning"}>{describe(todo)}</StatusText>
            {action ? (
              <Button size="small" icon={action.icon} onClick={action.onClick}>
                {action.label}
              </Button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
